import { useEffect, useRef } from "react";

export default function useWebXRAR(
  renderer,
  scene,
  camera,
  inAR,
  cube,
  planeMesh,
  setARMessage,
  verticalOffset,
  videoRef
) {
  const hitTestSourceRef = useRef(null);
  const xrRefSpaceRef = useRef(null);

  useEffect(() => {
    if (!renderer || !scene || !camera || !inAR || !planeMesh || !videoRef?.current) return;

    const startAR = async () => {
      if (!navigator.xr) {
        setARMessage("❌ WebXR not supported.");
        return;
      }

      const supported = await navigator.xr.isSessionSupported("inline-ar");
      if (!supported) {
        setARMessage("⚠️ inline AR not supported.");
        return;
      }

      const xrSession = await navigator.xr.requestSession("inline-ar", {
        requiredFeatures: ["hit-test", "local-floor"],
      });

      renderer.xr.setSession(xrSession);

      xrRefSpaceRef.current = await xrSession.requestReferenceSpace("local-floor");
      const viewerSpace = await xrSession.requestReferenceSpace("viewer");
      hitTestSourceRef.current = await xrSession.requestHitTestSource({ space: viewerSpace });

      renderer.setAnimationLoop((time, frame) => {
        if (!frame) return;

        const hitResults = frame.getHitTestResults(hitTestSourceRef.current);
        if (hitResults.length > 0) {
          const pose = hitResults[0].getPose(xrRefSpaceRef.current);

          if (planeMesh) {
            planeMesh.rotation.x = -Math.PI / 2;
            planeMesh.visible = true;
            planeMesh.position.set(
              pose.transform.position.x,
              pose.transform.position.y + verticalOffset,
              pose.transform.position.z
            );
            planeMesh.quaternion.set(
              pose.transform.orientation.x,
              pose.transform.orientation.y,
              pose.transform.orientation.z,
              pose.transform.orientation.w
            );
          }

          if (cube && cube.visible) {
            cube.position.set(
              pose.transform.position.x,
              pose.transform.position.y + verticalOffset,
              pose.transform.position.z
            );
          }
        } else {
          if (planeMesh) planeMesh.visible = false;
        }

        renderer.render(scene, camera);
      });
    };

    startAR();
  }, [renderer, scene, camera, inAR, cube, planeMesh, verticalOffset, videoRef]);

  return {};
}
